import {
  createSite as _createSite,
  deleteSite as _deleteSite,
  setCurrentSiteId as _setCurrentSiteId,
  updateSite as _updateSite,
} from '@jwplayer/companion/sites';
import { clearCurrentSiteId } from '@jwplayer/companion/sites/cache';
import { auth, type GetOutput, type GetParams, jwApi } from '@jwplayer/companion-gateway';

import { REGEX_ACCOUNT_UUID } from '@jwplayer/fe-common/constants/regex';
import { createCookieManager } from '@jwplayer/fe-common/module/cookies';
import { getWuiOverlaysApi } from '@jwplayer/wui-react';
import { queryCache } from 'react-query';

import { sitesNameCache } from '/component/site-select-input';
import { SessionStore } from '/global-state';
import { updateCurrentUser } from '/global-state/session/actions';

import { redirect } from '../routing/module';
import { setCurrentSiteTag } from '../sentry';

type Site = GetOutput<'sites', 'read'>;

const cookieManager = createCookieManager({
  Path: '/',
  Secure: true,
  SameSite: 'strict',
});

const LAST_SITE_ID_COOKIE = () => {
  const name = `jw_active_site_id_${auth.getCache()?.account_id}`;
  if (import.meta.env.APP_JW_ENV !== 'prd') {
    return `${name}_${import.meta.env.APP_JW_ENV}`;
  }
  return name;
};

export function setCurrentSiteId(siteId?: string, fallbackToDefaultSiteId?: string | true) {
  function _set(id: string | undefined) {
    if (import.meta.env.APP_NODE_ENV === 'development') {
      // biome-ignore lint/suspicious/noConsole: dev only
      console.debug('Setting current site', siteId);
    }

    SessionStore.update((s) => {
      s.currentSiteId = id;
    });

    setCurrentSiteTag(id);

    if (typeof id === 'undefined') {
      clearCurrentSiteId(); // update companion v1
      cookieManager.deleteCookie(LAST_SITE_ID_COOKIE());
    } else {
      _setCurrentSiteId(id); // update companion v1
      cookieManager.setCookie(LAST_SITE_ID_COOKIE(), id);
    }

    return id;
  }

  if (!siteId) {
    return _set(undefined);
  }

  try {
    return _set(siteId);
  } catch (err: unknown) {
    if (!fallbackToDefaultSiteId) {
      throw err;
    }

    if (fallbackToDefaultSiteId === true) {
      return _set(auth.getCache()?.default_site);
    }
    return _set(fallbackToDefaultSiteId);
  }
}

const getQueryKey = (siteId: string) => ['site', siteId];

export async function deleteSite(siteId: string, abortController?: AbortController) {
  // Remove from (query) cache first
  sitesNameCache.delete(siteId);
  queryCache.removeQueries(getQueryKey(siteId), { exact: true });

  const state = SessionStore.getState();

  // Update current site
  if (state.currentSiteId === siteId) {
    const defaultSiteId = state.session?.default_site;

    // Either use default site id, or any other site from the cache as the new site id
    const newActiveSiteId = siteId === defaultSiteId ? sitesNameCache.getIds().at(0) : defaultSiteId;

    if (!newActiveSiteId) {
      throw new Error('Could not find new site to activate!');
    }

    SessionStore.setState((s) => {
      s.currentSiteId = newActiveSiteId;
    });
  }

  // Ensure we're back on the list page, because the detail page will be invalid once we delete the site
  redirect({
    to: {
      pathname: 'accountSitesList',
      params: { tabName: 'properties' },
    },
  });

  await jwApi(
    'sites',
    'delete',
    {
      routeParams: {
        siteId,
      },
    },
    { signal: abortController?.signal },
  );

  // If user preference is using deleted site
  if (state.user?.metadata.settings.default_property === siteId) {
    // Revert to 'recent'
    await updateCurrentUser({
      settings: {
        default_property: 'recent',
      },
    });
  }
}

export async function createSite(input: GetParams<'sites', 'create'>['input'], abortController?: AbortController) {
  const newSite = (
    await jwApi(
      'sites',
      'create',
      {
        input,
      },
      {
        signal: abortController?.signal,
      },
    )
  ).data;

  sitesNameCache.set(newSite.id, { id: newSite.id, name: newSite.metadata.name });

  // Update query cache in advance to save an api call later
  queryCache.setQueryData<Site>(getQueryKey(newSite.id), newSite);
}

export async function enableDrmOnSite(siteId: string, abortController?: AbortController) {
  // Enable DRM on the site after creating it
  await jwApi(
    'sites',
    'enable-drm',
    {
      routeParams: {
        siteId,
      },
    },
    { signal: abortController?.signal },
  );
}

export async function updateSite(
  siteId: string,
  newData: GetParams<'sites', 'update'>['input'],
  opts?: { abortController?: AbortController },
) {
  const queryKey = getQueryKey(siteId);

  queryCache.cancelQueries(queryKey);

  const existingData = queryCache.getQueryData<Site>(queryKey);

  if (existingData) {
    // Optimistically update first
    queryCache.setQueryData<Site, Site>(queryKey, {
      ...existingData,
      ...newData,

      metadata: {
        ...existingData.metadata,
        ...newData.metadata,
      },
    });
  }

  try {
    const updatedData = await (
      await jwApi(
        'sites',
        'update',
        {
          routeParams: {
            siteId,
          },
          input: newData,
        },
        {
          signal: opts?.abortController?.signal,
        },
      )
    ).data;

    sitesNameCache.set(updatedData.id, { id: updatedData.id, name: updatedData.metadata.name });

    getWuiOverlaysApi().Toast.createAndOpen({ msg: 'Updated property settings' });

    return updatedData;
  } catch (err: unknown) {
    // Revert to previous value
    queryCache.setQueryData(queryKey, existingData);

    getWuiOverlaysApi().Toast.createAndOpen({ msg: 'Failed to update property settings', type: 'error' });

    console.error(err);
    throw err;
  }
}

function findSiteId(token: string | undefined, session: GetOutput<'dashboard-scope', 'read'>) {
  if (token?.match(REGEX_ACCOUNT_UUID)) {
    return session.sites.find((s) => s.token === token)?.site_id;
  }
  // Ensure that the site exists
  return session.sites.find((s) => s.site_id === token)?.site_id;
}

export function getInitialActiveSiteId(
  session: GetOutput<'dashboard-scope', 'read'>,
  user: GetOutput<'current-user', 'read'>,
): string {
  const lastUsedSiteId = cookieManager.getCookie(LAST_SITE_ID_COOKIE())?.trim();

  const userPreferenceDefaultProperty = user.metadata.settings.default_property;

  // If user want to use the most recent site
  if (userPreferenceDefaultProperty === 'recent') {
    // Return last used site id, or fallback to default site
    return findSiteId(lastUsedSiteId, session) ?? session.default_site;
  }

  return findSiteId(userPreferenceDefaultProperty, session) ?? session.default_site;
}
